/**
 * BATCH 6C Part 4 — Per-Person Pricing Audit Tool.
 * Read-only, admin-only forensic report: finds itineraries priced
 * per_person where the linked trip's pax != 2 and the trip's baseCost was
 * derived from the itinerary — these may still carry the OLD buggy
 * `price * 2` baseCost from before the 2026-08-26 per-person pax fix.
 * Zero writes anywhere on this page — pure list + flag.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export default function PricingAudit() {
  const [data, setData] = useState({ count: 0, rows: [], note: '' });
  const [loading, setLoading] = useState(true);
  // NOTE: keep this as a plain local identifier, not `data.rows` inline in
  // JSX — `data.rows.map(...)` (a member-expression array source) trips an
  // infinite-recursion bug in this repo's visual-edits babel plugin.
  const auditRows = data.rows || [];

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/pricing-audit`, { withCredentials: true })
      .then((r) => setData(r.data || { count: 0, rows: [] }))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load pricing audit'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="pricing-audit-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-amber-600" /> Per-Person Pricing Audit
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Read-only. Flags trips priced per-person (pax ≠ 2) whose base cost was synced from the itinerary —
          candidates for the pre-2026-08-26 <code className="text-[11px] bg-slate-100 px-1 rounded">price × 2</code> bug. Nothing here is ever auto-fixed.
        </p>
      </header>

      <Card className="p-4 flex items-center justify-between border border-slate-200" data-testid="pricing-audit-summary-card">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Flagged trips</span>
        <span className="text-xl font-bold font-mono text-slate-900" data-testid="pricing-audit-count">{data.count}</span>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : auditRows.length === 0 ? (
        <Card className="p-12 text-center" data-testid="pricing-audit-empty">
          <ShieldCheck className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">No anomalies found — every itinerary-synced per-person trip has pax = 2, or wasn't itinerary-synced.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="pricing-audit-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pricing-audit-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Trip</th>
                  <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Itinerary</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Pax</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Price / Person</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Current Base Cost</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Old Buggy (×2)</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Correct (×pax)</th>
                  <th className="px-4 py-2 text-center text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Sent to Client?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {auditRows.map((r) => <AuditRow key={r.itineraryId} r={r} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {data.note && <p className="text-[11px] text-slate-400" data-testid="pricing-audit-note">{data.note}</p>}
    </div>
  );
}

const AuditRow = ({ r }) => {
  const matchesBuggy = Math.abs((r.currentBaseCost || 0) - (r.oldBuggyValue || 0)) < 1;
  const rowClass = matchesBuggy ? 'bg-rose-50/50' : '';
  const buggyCellClass = matchesBuggy ? 'text-rose-700 font-bold' : 'text-slate-400';
  return (
    <tr data-testid={`pricing-audit-row-${r.itineraryId}`} className={rowClass}>
      <td className="px-4 py-2">
        <Link to={`/quotes/${encodeURIComponent(r.tripId)}/edit`} className="text-teal-700 hover:underline font-mono text-xs">{r.tripId}</Link>
        <div className="text-[10px] text-slate-400">{r.tripStatus}</div>
      </td>
      <td className="px-4 py-2 text-xs text-slate-600">{r.itineraryTitle || r.publicRef}</td>
      <td className="px-4 py-2 text-right font-mono">{r.pax}</td>
      <td className="px-4 py-2 text-right font-mono">{inr(r.pricePerPerson)}</td>
      <td className="px-4 py-2 text-right font-mono font-semibold">{inr(r.currentBaseCost)}</td>
      <td className={`px-4 py-2 text-right font-mono ${buggyCellClass}`}>{inr(r.oldBuggyValue)}</td>
      <td className="px-4 py-2 text-right font-mono text-emerald-700">{inr(r.correctValue)}</td>
      <td className="px-4 py-2 text-center">
        <SentBadge sent={r.everSentToClient} />
      </td>
    </tr>
  );
};

const SentBadge = ({ sent }) => {
  if (sent) return <Badge className="bg-rose-100 text-rose-700 text-[10px]">Yes — review</Badge>;
  return <Badge className="bg-slate-100 text-slate-500 text-[10px]">No</Badge>;
};
